import {
  useEffect,
  useRef,
  useState
} from 'react'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import {
  Image,
  Stack
} from '@chakra-ui/react'
import { useSearchParams } from 'react-router-dom'
import { ImagesPng } from 'assets/images'
import { motion } from 'framer-motion'
import L from 'leaflet'
import {
  authSelectors,
  chatCredentialsSelectors,
  getNotificationMessage,
  handleOpenModal,
  markersSelectors,
  setBlogParent,
  getComments,
  setComments,
  setIsBlogFormOpen,
  setIsProfileSwitched,
  setLiveLocData,
  setOpenBlogForm,
  setUserSectionPage,
  usersSelectors,
  setExecAutoArchive,
  setBlogRepostData,
  setClosePane,
  setOnPostEditor,
  setChatSectionPage,
  setOpenPopupBlogs,
  setOpenPopupEvents,
  setNotificationSectionPage
} from 'store'
import {
  liveMarkerUpdate,
  staticMarkerUpdate
} from 'store/markers/axios'
import {
  BLOGS,
  EDIT_PROFILE,
  FORGOT_PASSWORD,
  MODAL_SECTION,
  MY_PROFILES,
  REJECTED,
  SETTINGS,
  SIGN_IN,
  SIGN_UP,
  LOCATION_TYPE,
  PENDING,
  PLATFORM_LIST
} from 'utils/constants'
import {
  isAuth,
  isNotMobile
} from 'utils/helpers'
import { COLORS } from 'utils/styles'

/* eslint-disable import/no-cycle */
import { Messenger } from 'components'
import { SearchContainer } from 'components/SearchContainer'
import { UserPanel } from 'components/Header/UserPanel'

const MotionStack = motion(Stack)

export const Header = () => {
  const { verificationEmailStandBy } = useSelector(authSelectors)
  const { xmppLiveLocation } = useSelector(chatCredentialsSelectors)
  const {
    user,
    activeProfile,
    blogParent,
    getCommentsStatus,
    isBlogFormOpen,
    isCloseModal,
    isProfileChanged,
    isProfileSwitched,
    userSectionPage,
    onPostEditor,
    chosenBlogIndex,
    closePane,
    closeAllDrawers
  } = useSelector(usersSelectors)
  const {
    liveMarkerExp,
    staticMarkerExp
  } = useSelector(markersSelectors)

  const [searchParams, setSearchParams] = useSearchParams()
  const btnRef = useRef()
  const dispatch = useDispatch()
  const [isDiscard, setIsDiscard] = useState(false)
  const [isClickedOutside, setIsClickedOutside] = useState(true)
  const [liveReset, setLiveReset] = useState(false)
  const [staticReset, setStaticReset] = useState(false)
  const [updateReset, setUpdateReset] = useState(false)
  const isBlogForMobile = () => isBlogFormOpen && !isNotMobile()
  const [blogFinishAnimation, setBlogFinishAnimation] = useState(false)
  const isOnEditProfile = userSectionPage.at(-1) === EDIT_PROFILE
    || userSectionPage.at(-1) === PLATFORM_LIST

  let intervalRef = useRef()
  const usePhoto = activeProfile && activeProfile.profile_picture
  if (localStorage.getItem('isLocDisabled') === null) {
    localStorage.setItem('isLocDisabled', false)
  }

  const goToHeaderOriginalPos = (parentData) => {
    const drawer = document.querySelector(`#${BLOGS}-drawer-body`)
    const targetElement = document.querySelector('#blogStartItem' + chosenBlogIndex)

    if (
      drawer
      && parentData?.parent_type
    ) {
      drawer.scrollTo({
        top: 0,
        behavior: 'auto'
      })
    } else if (
      drawer
      && targetElement
    ) {
      requestAnimationFrame(() => {
        const containerTop = drawer.getBoundingClientRect().top
        const elementTop = targetElement.getBoundingClientRect().top
        const scrollDifference = Math.floor(elementTop) - containerTop

        drawer.scrollTop += scrollDifference
      })
    }
  }

  const handleCloseButton = () => {
    if (
      isAuth()
      && user
    ) {
      if (userSectionPage.at(-1) === MY_PROFILES) {
        dispatch(setUserSectionPage(null))
      } else if (
        isProfileChanged === true
        && userSectionPage.at(-1) === EDIT_PROFILE
      ) {
        setIsDiscard(true)
      } else if (
        userSectionPage.at(-1) === BLOGS
        || (
          isProfileChanged === false
          && isOnEditProfile
        )
      ) {
        if (blogParent.length) {
          if (
            blogParent.length > 1
            && getCommentsStatus === PENDING
          ) {
            dispatch(getNotificationMessage({
              type: REJECTED,
              message: 'Please finish loading first before returning to previous blog post or comment',
              isEditProfile: true
            }))
          } else {
            const parentData = blogParent[blogParent.length - 1]

            goToHeaderOriginalPos(parentData)

            dispatch(setComments([]))
            if (parentData?.parent_type) {
              dispatch(getComments({
                type: parentData.parent_type,
                ...(parentData.parent_type === 'post' ? { post_id: parentData.post_id } : { comment_id: parentData.parent_comment_id })
              }))
            }
          }

          dispatch(setBlogParent(blogParent.slice(0, -1)))
        } else {
          dispatch(setUserSectionPage(null))
        }
      } else {
        dispatch(setUserSectionPage(null))
      }
    } else if (userSectionPage.at(-1) === SIGN_IN) {
      dispatch(setUserSectionPage('clear'))
    } else if (
      !isAuth()
      && !user
      && (
        userSectionPage.at(-1) === FORGOT_PASSWORD
        || userSectionPage.at(-1) === SIGN_UP
      )
    ) {
      dispatch(setUserSectionPage(null))
    } else {
      dispatch(setUserSectionPage(SIGN_IN))
    }
  }

  const handleSettingButton = () => {
    setIsClickedOutside(false)

    if (
      isProfileChanged === true
      && isOnEditProfile
    ) {
      setIsDiscard(true)
    } else if (
      userSectionPage.at(-1) === BLOGS
      && onPostEditor === 'post'
    ) {
      dispatch(setExecAutoArchive(true))
      dispatch(setOnPostEditor(''))
      setTimeout(() => dispatch(setUserSectionPage(SETTINGS)), 600)
    } else {
      dispatch(setUserSectionPage(SETTINGS))
    }
  }

  useEffect(() => {
    if (closePane) {
      dispatch(setClosePane(false))
      handleCloseButton()
    }
  }, [closePane])

  useEffect(() => {
    dispatch(setUserSectionPage('clear'))
  }, [isCloseModal])

  const getRandomDecimalWithPrecision = (min, max, precision) => {
    const factor = 10 ** precision
    min *= factor
    max *= factor
    return (Math.floor(Math.random() * (max - min + 1)) + min) / factor
  }

  // User Live Loc Update
  useEffect(() => {
    const activeID = activeProfile?.id
    const activeLocType = activeProfile?.location_type
    const activeCoordinates = activeProfile?.coordinates
    let addRadiusLat = 0
    let addRadiusLng = 0

    if (
      (
        activeID
        && (
          activeLocType?.[LOCATION_TYPE.LiveLocation] === 0
          || activeLocType?.[LOCATION_TYPE.LiveApproximate] === 1
        )
      )
      && Number.isNaN(xmppLiveLocation?.lat) === false
    ) {
      if (activeLocType?.[LOCATION_TYPE.LiveApproximate] === 1) {
        addRadiusLat = getRandomDecimalWithPrecision(-0.001, 0.001, 4)
        addRadiusLng = getRandomDecimalWithPrecision(-0.001, 0.001, 4)
      }

      let newCoords = {
        lat: Number((parseFloat(xmppLiveLocation?.lat) + addRadiusLat).toFixed(6)),
        lng: Number((parseFloat(xmppLiveLocation?.lng) + addRadiusLng).toFixed(6))
      }

      setTimeout(() => {
        if (
          activeCoordinates?.lat !== xmppLiveLocation?.lat
          && activeCoordinates?.lng !== xmppLiveLocation?.lng
          && sessionStorage.getItem('id') === null
        ) {
          const distance = (L.latLng(activeCoordinates)).distanceTo(L.latLng(xmppLiveLocation))

          // check whether the distance between the old and new coords is >= 10 meters
          if (distance >= 10) {
            dispatch(setLiveLocData({ id: activeID, coordinates: newCoords }))
          }
        }
        setUpdateReset(!updateReset)
      }, 60000)
    } else {
      setTimeout(() => setUpdateReset(!updateReset), 5000)
    }
  }, [updateReset])

  // Live Update Interval
  useEffect(() => {
    let currentDate = new Date().toLocaleString('en-US', { timeZone: 'UTC', hour12: false })
    if (liveMarkerExp) {
      let liveMarkerDate = new Date(liveMarkerExp).toLocaleString('en-US', { hour12: false })
      let northeast = JSON.parse(localStorage.getItem('northeast'))
      let southwest = JSON.parse(localStorage.getItem('southwest'))

      setTimeout(() => {
        if (
          currentDate >= liveMarkerDate
          && sessionStorage.getItem('id') === null
          && localStorage.getItem('isNotifOpen') === 'false'
        ) {
          dispatch(liveMarkerUpdate({
            isAuth: isAuth(),
            northeast,
            southwest
          }))
        }
        setLiveReset(!liveReset)
      }, 60000)
    } else {
      setTimeout(() => setLiveReset(!liveReset), 5000)
    }
  }, [liveReset])

  // Static Update Interval
  useEffect(() => {
    let currentDate = new Date().toLocaleString('en-US', { timeZone: 'UTC', hour12: false })
    if (staticMarkerExp) {
      let staticMarkerDate = new Date(staticMarkerExp).toLocaleString('en-US', { hour12: false })
      let northeast = JSON.parse(localStorage.getItem('northeast'))
      let southwest = JSON.parse(localStorage.getItem('southwest'))

      setTimeout(() => {
        if (
          currentDate >= staticMarkerDate
          && sessionStorage.getItem('id') === null
          && localStorage.getItem('isNotifOpen') === 'false'
        ) {
          dispatch(staticMarkerUpdate({
            isAuth: isAuth(),
            northeast,
            southwest
          }))
        }
        setStaticReset(!staticReset)
      }, 60000)
    } else {
      setTimeout(() => setStaticReset(!staticReset), 5000)
    }
  }, [staticReset])

  useEffect(() => {
    if (isProfileSwitched === true) {
      clearInterval(intervalRef.current)
      dispatch(setIsProfileSwitched(false))
    }
  }, [isProfileSwitched])

  useEffect(() => {
    setTimeout(() => setBlogFinishAnimation(isBlogForMobile()), 350)
  }, [isBlogFormOpen])

  useEffect(() => {
    if (closeAllDrawers) {
      dispatch(setUserSectionPage('clear'))
    }
  }, [closeAllDrawers])

  useEffect(() => {
    if (verificationEmailStandBy) {
      dispatch(setUserSectionPage('clear'))
    }
  }, [verificationEmailStandBy])

  return (
    <>
      <MotionStack
        background={COLORS.gold}
        className="header"
      >
        <MotionStack
          initial={{ y: 0 }}
          animate={{ y: isBlogForMobile() ? -50 : 0 }}
          transition={{
            type: 'tween',
            duration: 0.8
          }}
          display="flex"
          justifyContent="center"
          paddingBlock="9px"
          spacing="10px"
          direction="row"
          alignItems="center"
          background={COLORS.gold}
        >
          {isAuth() ? (
            <Image
              boxSize="32px"
              cursor="pointer"
              objectFit="cover"
              src={usePhoto || ImagesPng.UserImage}
              alt="Logo"
              borderRadius="full"
              zIndex={1401}
              onClick={() => {
                sessionStorage.removeItem('openProfileAfterAnimation')
                sessionStorage.removeItem('openEventAfterAnimation')

                dispatch(setUserSectionPage('clear'))
                dispatch(setIsBlogFormOpen(false))
                dispatch(setOpenBlogForm(false))
                document.querySelector('#custom-popup-overlay')?.click()
                document.querySelector('#custom-popup-overlay-event')?.click()
                dispatch(setChatSectionPage('clear'))
                dispatch(setOpenPopupBlogs(false))
                dispatch(setOpenPopupEvents(false))
                dispatch(setNotificationSectionPage('clear'))
                dispatch(setBlogRepostData([]))
                // This is for map filter to move
                dispatch(handleOpenModal({ isOpen: false, section: MODAL_SECTION?.RightSection }))

                dispatch(setUserSectionPage(MY_PROFILES))
                setSearchParams({})
              }}
              ref={btnRef}
              className="profile-icon"
            />
          ) : (
            <Image
              as={motion.img}
              animate={{
                scale: [1, 1.1, 1],
                opacity: [1, 0.8, 1],
                transition: {
                  repeat: Infinity,
                  duration: 1
                }
              }}
              zIndex={1392}
              boxSize="32px"
              cursor="pointer"
              objectFit="cover"
              src={usePhoto || ImagesPng.UserImage}
              alt="Logo"
              borderRadius="full"
              onClick={() => {
                dispatch(setChatSectionPage('clear'))
                dispatch(setNotificationSectionPage('clear'))
                dispatch(setUserSectionPage('clear'))
                dispatch(setUserSectionPage(SIGN_IN))
              }}
              ref={btnRef}
              className="profile-icon"
            />
          )}

          <SearchContainer />

          <Messenger />
        </MotionStack>
      </MotionStack>

      <UserPanel
        isDiscard={isDiscard}
        setIsDiscard={setIsDiscard}
        blogFinishAnimation={blogFinishAnimation}
        isClickedOutside={isClickedOutside}
        setIsClickedOutside={setIsClickedOutside}
        isBlogForMobile={isBlogForMobile}
        handleCloseButton={handleCloseButton}
        handleSettingButton={handleSettingButton}
      />
    </>
  )
}
